import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  PhotoCamera,
  Close,
  Refresh,
  Check,
  Cancel
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import { saveVisitorImageWithMetadata } from '../db/saveVisitorImageWithMetadata';

const CameraCapture = ({
  open,
  onClose,
  onPhotoCapture,
  visitorName = 'Visitor'
}) => {
  const { user } = useAuth();
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const webcamRef = useRef(null);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user"
  };

  const onUserMedia = useCallback(() => {
    setCameraReady(true);
    setCameraError(null);
  }, []);

  const onUserMediaError = useCallback((error) => {
    console.error('Camera error:', error);
    let message = 'Unable to access camera. Please ensure camera permissions are granted.';
    if (error && error.name) {
      if (error.name === 'NotAllowedError') {
        message = 'Camera access was denied. Please allow camera permissions in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        message = 'No camera device found. Please connect a camera and try again.';
      } else if (error.name === 'AbortError') {
        message = 'Camera could not be started (timeout). Please check your device and try again.';
      } else if (error.message) {
        message = error.message;
      }
    }
    setCameraError(message);
    setCameraReady(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedPhoto(imageSrc);
    }
  }, []);

  const confirmPhoto = async () => {
    if (!capturedPhoto || !user) return;
    try {
      setIsUploading(true);
      // Save image to storage and Firestore
      const downloadURL = await saveVisitorImageWithMetadata(
        capturedPhoto,
        user?.uid || 'unknown-visitor',
        { name: user?.displayName || '', email: user?.email || '' }
      );
      if (onPhotoCapture) {
        onPhotoCapture(downloadURL);
      }
      handleClose();
    } catch (error) {
      console.error('Error uploading photo to Firebase:', error);
      setCameraError('Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setCameraError(null);
  };

  const handleClose = () => {
    setCapturedPhoto(null);
    setCameraError(null);
    setCameraReady(false);
    setIsUploading(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      setCapturedPhoto(null);
      setCameraError(null);
      setCameraReady(false);
      setIsUploading(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Take Photo - {visitorName}</Typography>
          <Typography variant="body2" color="textSecondary">
            Please ensure good lighting and look directly at the camera
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={isUploading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', minHeight: 500 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          📷 <strong>Camera Access Required:</strong> Allow permissions and position yourself clearly.
        </Alert>

        {cameraError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {cameraError}
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
                Retry Camera
              </Button>
            </Box>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              Tip: Check your browser address bar for a camera icon or permission prompt. If denied, enable camera access in browser settings and reload the page.
            </Typography>
          </Alert>
        )}

        {!capturedPhoto ? (
          <Box>
            <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Webcam
                ref={webcamRef}
                audio={false}
                height={400}
                width="100%"
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMedia={onUserMedia}
                onUserMediaError={onUserMediaError}
                style={{
                  borderRadius: 8,
                  maxWidth: '100%',
                  height: 'auto'
                }}
              />

              {!cameraReady && !cameraError && (
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  p: 2,
                  borderRadius: 1
                }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2">Starting camera...</Typography>
                </Box>
              )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PhotoCamera />}
                onClick={capturePhoto}
                disabled={!cameraReady || cameraError}
                sx={{ minWidth: 150 }}
              >
                {cameraReady ? 'Capture Photo' : 'Camera Loading...'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>Photo Preview</Typography>

            <Paper elevation={3} sx={{ p: 2, mb: 3, display: 'inline-block' }}>
              <img
                src={capturedPhoto}
                alt="Captured visitor"
                style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }}
              />
            </Paper>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              This photo will be uploaded securely to cloud storage.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={retakePhoto}
                disabled={isUploading}
              >
                Retake Photo
              </Button>
              <Button
                variant="contained"
                startIcon={isUploading ? <CircularProgress size={20} /> : <Check />}
                onClick={confirmPhoto}
                disabled={isUploading}
                sx={{ minWidth: 140 }}
              >
                {isUploading ? 'Uploading...' : 'Save Photo'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} startIcon={<Cancel />} disabled={isUploading}>
          Cancel
        </Button>

        {capturedPhoto && (
          <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, textAlign: 'center' }}>
            Photo will be uploaded to Firebase Storage
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CameraCapture;
